import type {
	DisconnectCalendarIntegrationResponse,
	DisconnectVideoConferencingResponse,
	GetMeetingsIntegrationsAvailableProvidersResponse,
	GetMeetingsIntegrationsSettingsResponse,
	GetUserCalendarIntegrationStatusResponse,
	GetUserCalendarsResponse,
	GetUserVideoConferencingStatusResponse,
	InitiateCalendarIntegrationOAuthFlowResponse,
	InitiateVideoConferencingOAuthFlowResponse,
	PatchMeetingsIntegrationsSettingsRequest,
	PatchMeetingsIntegrationsSettingsResponse,
	SaveSelectedCalendarRequest,
	SaveSelectedCalendarResponse,
	VideoConferencingProviderEnum,
} from "@performyard/service-contracts";

import request from "./internal-base-request";

export const getMeetingsIntegrationsSettings = async (): Promise<GetMeetingsIntegrationsSettingsResponse> => {
	const [response] = await request<GetMeetingsIntegrationsSettingsResponse>("/v1/meetings-integrations/settings");
	return response;
};

export const updateMeetingsIntegrationsSettings = async (
	data: PatchMeetingsIntegrationsSettingsRequest
): Promise<PatchMeetingsIntegrationsSettingsResponse> => {
	const [response] = await request<PatchMeetingsIntegrationsSettingsResponse>("/v1/meetings-integrations/settings", {
		method: "PATCH",
		body: JSON.stringify(data),
	});
	return response;
};

export const getCalendarIntegrationOAuthUrl = async ({
	provider,
}: {
	provider: "google" | "outlook";
}): Promise<InitiateCalendarIntegrationOAuthFlowResponse> => {
	const [data] = await request<InitiateCalendarIntegrationOAuthFlowResponse>(
		`/v1/meetings-integrations/oauth/initiate/${provider}`
	);
	return data;
};

export const getUserCalendarIntegrationStatus = async (): Promise<GetUserCalendarIntegrationStatusResponse> => {
	const [response] = await request<GetUserCalendarIntegrationStatusResponse>("/v1/meetings-integrations/status");
	return response;
};

/**
 * Unified method to get user calendars from any provider
 */
export const getUserCalendars = async ({
	provider,
}: {
	provider: "google" | "outlook";
}): Promise<GetUserCalendarsResponse> => {
	const [response] = await request<GetUserCalendarsResponse>(`/v1/meetings-integrations/${provider}/calendars`);
	return response;
};

export const saveSelectedCalendar = async (
	data: SaveSelectedCalendarRequest
): Promise<SaveSelectedCalendarResponse> => {
	const [response] = await request<SaveSelectedCalendarResponse>("/v1/meetings-integrations/calendar/select", {
		method: "POST",
		body: JSON.stringify(data),
	});
	return response;
};

export const disconnectCalendarIntegration = async (): Promise<DisconnectCalendarIntegrationResponse> => {
	const [response] = await request<DisconnectCalendarIntegrationResponse>("/v1/meetings-integrations/disconnect", {
		method: "DELETE",
	});
	return response;
};

// ---------- Video Conferencing ----------

export const getMeetingsIntegrationsAvailableProviders =
	async (): Promise<GetMeetingsIntegrationsAvailableProvidersResponse> => {
		const [response] = await request<GetMeetingsIntegrationsAvailableProvidersResponse>(
			"/v1/meetings-integrations/providers"
		);
		return response;
	};

export const getUserVideoConferencingStatus = async (
	includeUserSettings?: boolean
): Promise<GetUserVideoConferencingStatusResponse> => {
	const query = includeUserSettings ? "?includeUserSettings=true" : "";
	const [response] = await request<GetUserVideoConferencingStatusResponse>(
		`/v1/meetings-integrations/video-conferencing/status${query}`
	);
	return response;
};

export const getVideoConferencingOAuthUrl = async ({
	provider,
}: {
	provider: VideoConferencingProviderEnum;
}): Promise<InitiateVideoConferencingOAuthFlowResponse> => {
	const [response] = await request<InitiateVideoConferencingOAuthFlowResponse>(
		`/v1/meetings-integrations/video-conferencing/oauth/initiate/${provider}`
	);
	return response;
};

export const disconnectVideoConferencingIntegration = async ({
	provider,
}: {
	provider: VideoConferencingProviderEnum;
}): Promise<DisconnectVideoConferencingResponse> => {
	const [response] = await request<DisconnectVideoConferencingResponse>(
		`/v1/meetings-integrations/video-conferencing/${provider}/disconnect`,
		{
			method: "DELETE",
		}
	);
	return response;
};
